import React, { useState, useEffect } from "react";
import { Check, X, ChevronDown, ChevronUp } from "lucide-react";
import toastr from 'toastr';

import useDocumentTitle from '../../hooks/useDocumentTitle';
import ShimmerLoader from '../../hooks/ShimmerLoader';
import Pagination from "../../hooks/Pagination";
import { getvoucherapprovals, reviewVoucher } from "../../services/api";

const TABS = [
  { value: 0, label: 'Pending' },
  { value: 2, label: 'Rejected' },
  { value: 1, label: 'Approved' },
];
const redeemLabel = { 1: 'Online', 2: 'Offline', 3: 'Both' };

export default function VoucherApprovals() {
  useDocumentTitle('Welcome to Admin | Voucher Approvals');
  const perPage = 20;

  const [status, setStatus] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expanded, setExpanded] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewing, setReviewing] = useState(false);

  toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: 'toast-top-center' };

  const fetchList = async () => {
    try {
      setLoading(true);
      const response = await getvoucherapprovals(perPage, currentPage, status);
      const data = await response.data;
      setRows(data.data || data);
      setCurrentPage(data.current_page);
      setTotalPages(data.last_page);
    } catch (err) {
      toastr.error(err.message || 'Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [currentPage, status]);

  const changeTab = (value) => {
    setStatus(value);
    setCurrentPage(1);
    setExpanded(null);
  };

  const submitReview = async (item, action, reason = '') => {
    if (action === 'reject' && !reason.trim()) {
      toastr.error('Reject reason is required', { timeOut: 2000 });
      return;
    }
    try {
      setReviewing(true);
      const response = await reviewVoucher({ id: item.id, action, reject_reason: reason.trim() });
      if (response.status.success) {
        toastr.success(response.message);
        setRejectTarget(null);
        setRejectReason('');
        fetchList();
      } else {
        toastr.error(response.message);
      }
    } catch (err) {
      toastr.error('Action failed. Please try again.');
    } finally {
      setReviewing(false);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '-');

  return (
    <div className="w-full bg-white shadow-lg rounded-2xl p-6">
      <h2 className="text-2xl font-bold mb-4">Voucher Approvals</h2>

      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => changeTab(t.value)}
            className={`px-4 py-2 rounded-xl text-sm transition-colors ${
              status === t.value ? 'bg-[#f4a406] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-gray-700 bg-gray-100">
            <tr>
              <th className="p-3">ID</th>
              <th className="p-3">Voucher Name</th>
              <th className="p-3">Store</th>
              <th className="p-3">Membership</th>
              <th className="p-3">No of voucher</th>
              <th className="p-3">Dis. value</th>
              <th className="p-3">Voucher price</th>
              <th className="p-3">Max dis. & Min purchase</th>
              <th className="p-3">Submitted</th>
              {status === 2 && <th className="p-3">Reject reason</th>}
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td className="p-3" colSpan={12}>
                  <ShimmerLoader />
                  <ShimmerLoader />
                  <ShimmerLoader />
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((item) => (
                <React.Fragment key={item.id}>
                  <tr>
                    <td className="p-3">{item.id}</td>
                    <td className="p-3">{item.voucher_name}</td>
                    <td className="p-3">{item.store_name}</td>
                    <td className="p-3">{item.membership_names || '-'}</td>
                    <td className="p-3">{item.no_of_voucher}</td>
                    <td className="p-3" nowrap="true">{item.discount_value}{Number(item.discount_type) === 2 ? ' %' : ' Fixed'}</td>
                    <td className="p-3">{item.voucher_price}</td>
                    <td className="p-3"><span>{item.max_discount_value}  <td className="">{item.min_purchase_value}</td></span></td>
                   
                    <td className="p-3" nowrap="true">{item.createdDate}</td>
                    {status === 2 && (
                      <td className="p-3 text-red-600 max-w-[240px] whitespace-normal">{item.reject_reason}</td>
                    )}
                    <td className="p-3 text-center space-x-3" nowrap="true">
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                        className="text-gray-600 hover:underline inline-flex items-center gap-1"
                        title="Details"
                      >
                        {expanded === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {status === 0 && (
                        <>
                          <button
                            type="button"
                            disabled={reviewing}
                            onClick={() => window.confirm(`Approve "${item.voucher_name}"? It will go live immediately.`) && submitReview(item, 'approve')}
                            className="text-green-600 hover:underline inline-flex items-center gap-1"
                            title="Approve"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => { setRejectTarget(item); setRejectReason(''); }}
                            className="text-red-600 hover:underline inline-flex items-center gap-1"
                            title="Reject"
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {expanded === item.id && (
                    <tr className="bg-gray-50">
                      <td className="p-4" colSpan={12}>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div><b>Redeem:</b> {redeemLabel[item.redemption_type] || '-'}</div>
                          <div><b>Customer / Owner share:</b> {item.customer_share}% / {item.owner_share}%</div>
                          <div><b>Valid from:</b> {fmtDate(item.valid_from)}</div>
                          <div><b>Valid to:</b> {fmtDate(item.valid_to)}</div>
                          <div className="md:col-span-4">
                            <b>Terms &amp; Conditions:</b>
                            <p className="whitespace-pre-wrap mt-1">{item.terms_condition}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td className="p-3" colSpan={12}>No record found</td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
      </div>

      {rejectTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4 font-semibold">Reject voucher: {rejectTarget.voucher_name}</div>
            <div className="p-6">
              <label className="block text-sm font-medium mb-1">
                Reject reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Tell the store why this voucher was rejected"
                className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
              />
            </div>
            <div className="flex gap-3 border-t px-6 py-4">
              <button type="button" onClick={() => setRejectTarget(null)} className="flex-1 border py-2 rounded-xl">
                Cancel
              </button>
              <button
                type="button"
                disabled={reviewing}
                onClick={() => submitReview(rejectTarget, 'reject', rejectReason)}
                className="flex-1 bg-red-600 text-white py-2 rounded-xl hover:bg-red-700"
              >
                {reviewing ? 'Please wait...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}